import React, { useState } from 'react';
import {
  Box, Typography, Divider, Chip, TextField, Alert, Stack,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HistoryIcon from '@mui/icons-material/History';
import { Referral, ReferralDocument, PriorEncounter } from '~/types/transition';

interface SummarySection {
  title: string;
  narrative: string;
  sources: string[];
  severity?: 'warning' | 'error';
}

function formatSourceLabel(doc: ReferralDocument): string {
  const date = new Date(doc.receivedAt).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
  return `${doc.type} (${doc.source}, ${date})`;
}

function buildIntakeSourceLabel(referral: Referral): string {
  const date = new Date(referral.receivedAt).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
  const labels: Record<string, string> = {
    EMAIL: 'Referral Email',
    EFAX: 'Referral Order (E-Fax)',
    FAX: 'Referral Order (Fax)',
    PHONE: 'Phone Intake Form',
    API: 'API Payload',
  };
  return `${labels[referral.source] ?? 'Referral Document'} (${date})`;
}

const IN_NETWORK_CARRIERS = new Set([
  'Medicare', 'Medicaid', 'Aetna', 'Humana', 'UnitedHealthcare',
  'BlueCross BlueShield', 'Cigna', 'Anthem',
]);

function buildSections(referral: Referral): SummarySection[] {
  const { patient } = referral;
  const additionalDocs = referral.additionalDocuments ?? [];
  const intakeSource = buildIntakeSourceLabel(referral);
  const sections: SummarySection[] = [];

  const clinicalNotes = additionalDocs.find((d) => d.type === 'Clinical Notes');
  const woundAssessment = additionalDocs.find((d) => d.type === 'Wound Assessment');
  const dischargeSummary = additionalDocs.find((d) => d.type === 'Discharge Summary');

  // Clinical Picture
  const diagnosisText = patient.diagnosisLabels.length > 1
    ? `${patient.diagnosisLabels[0]} and ${patient.diagnosisLabels.slice(1).join(', ')}`
    : (patient.diagnosisLabels[0] ?? 'undocumented diagnosis');

  let clinicalNarrative = `${patient.name} is referred for ${patient.requestedProgram} services with a primary diagnosis of ${diagnosisText}.`;
  if (clinicalNotes) {
    clinicalNarrative += ' Per clinical progress notes, the patient is medically stable with minimal assistance required for ambulation and vital signs within normal limits.';
  } else {
    clinicalNarrative += ' Based on the referral documentation, the patient is described as medically stable and appropriate for home-based care.';
  }
  if (woundAssessment) {
    clinicalNarrative += ' A wound assessment has been received and identifies findings that require clinical review before care begins.';
  }

  sections.push({
    title: 'Clinical Picture',
    narrative: clinicalNarrative,
    sources: [intakeSource, ...(clinicalNotes ? [formatSourceLabel(clinicalNotes)] : [])],
  });

  // Wound Status — only when wound assessment has arrived
  if (woundAssessment) {
    sections.push({
      title: 'Wound Status',
      narrative: 'The wound assessment identifies a full-thickness post-surgical wound with tunneling (1.5 cm) and a MRSA-positive culture result. Moderate purulent exudate and surrounding erythema are present. These findings represent a level of wound complexity that may exceed standard home health wound care protocols. Clinical review is required before care begins to confirm appropriate staffing, infection control protocols, and care plan.',
      sources: [formatSourceLabel(woundAssessment)],
      severity: 'warning',
    });
  }

  // Discharge Planning — only when discharge summary has arrived and shows discrepancy
  if (dischargeSummary) {
    sections.push({
      title: 'Discharge Planning',
      narrative: `The discharge summary indicates the patient will recover at a family member's residence (5220 Mesa Dr, Pueblo, CO 81003) rather than the registered home address on the referral (${patient.address}, ${patient.city}, ${patient.state} ${patient.zip}). This discharge address falls outside the active service territory. The coordinator must confirm the final discharge location with the referring provider before a decision can be made.`,
      sources: [formatSourceLabel(dischargeSummary)],
      severity: 'error',
    });
  }

  // Insurance & Coverage
  const hasVerifiedId = !!patient.insuranceMemberId;
  const isInNetwork = IN_NETWORK_CARRIERS.has(patient.insurance);
  sections.push({
    title: 'Insurance & Coverage',
    narrative: `Insurance is listed as ${patient.insurance}${hasVerifiedId ? ` (Member ID: ${patient.insuranceMemberId})` : ' — member ID not yet on file'}. ${isInNetwork ? 'This carrier is in-network and coverage has been verified against program requirements.' : 'Coverage eligibility should be confirmed with the insurance carrier prior to start of care.'}`,
    sources: [intakeSource],
  });

  // Referring Provider
  sections.push({
    title: 'Referring Provider',
    narrative: `Referral submitted by ${patient.referringProvider} at ${patient.referringFacility}.${patient.referringPhone ? ` Phone: ${patient.referringPhone}.` : ''}${patient.referringFax ? ` Fax: ${patient.referringFax}.` : ''} Coordination with the referring provider is recommended at the start of care to ensure continuity of the care plan.`,
    sources: [intakeSource],
  });

  return sections;
}

interface ClinicalSummaryCardProps {
  referral: Referral;
}

const ClinicalSummaryCard: React.FC<ClinicalSummaryCardProps> = ({ referral }) => {
  const [coordinatorNotes, setCoordinatorNotes] = useState('');

  const sections = buildSections(referral);
  const additionalDocs = referral.additionalDocuments ?? [];
  const totalSources = 1 + additionalDocs.length;
  const lastDoc = additionalDocs[additionalDocs.length - 1];
  const lastUpdated = new Date(lastDoc?.receivedAt ?? referral.receivedAt).toLocaleDateString(
    'en-US', { month: 'short', day: 'numeric', year: 'numeric' }
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Attribution */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesomeIcon sx={{ color: 'primary.main', fontSize: '1rem' }} />
        <Typography variant="body2" color="text.secondary">
          Generated by AI from {totalSources} source{totalSources !== 1 ? 's' : ''} · Last updated {lastUpdated}
        </Typography>
      </Box>

      {/* Sections */}
      {sections.map((section) => (
        <Box key={section.title}>
          <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: 1.2 }}>
            {section.title}
          </Typography>
          <Divider sx={{ mt: 1, mb: 2, borderBottomWidth: 2 }} />

          {section.severity ? (
            <Alert severity={section.severity === 'error' ? 'error' : 'warning'} sx={{ mb: 1.5 }}>
              {section.narrative}
            </Alert>
          ) : (
            <Typography variant="body1" sx={{ lineHeight: 1.75, color: 'text.secondary', mb: 1.5 }}>
              {section.narrative}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.disabled" sx={{ mr: 0.25 }}>
              Sources:
            </Typography>
            {section.sources.map((src) => (
              <Chip
                key={src}
                label={src}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.6rem', height: 18 }}
              />
            ))}
          </Box>
        </Box>
      ))}

      {/* Prior Encounters */}
      {(referral.priorEncounters ?? []).length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0 }}>
            <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: 1.2 }}>
              Prior Encounters
            </Typography>
            <Chip
              icon={<HistoryIcon sx={{ fontSize: '0.75rem !important' }} />}
              label="Returning patient"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: '0.62rem', height: 20, fontWeight: 600 }}
            />
          </Box>
          <Divider sx={{ mt: 1, mb: 2, borderBottomWidth: 2 }} />

          {(referral.priorEncounters ?? []).some((e) => e.outcome === 'DECLINED') && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This patient was previously declined. Review the prior encounter details below and confirm that circumstances have changed before proceeding.
            </Alert>
          )}

          <Stack spacing={2}>
            {(referral.priorEncounters ?? []).map((enc: PriorEncounter) => (
              <Box
                key={enc.id}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: enc.outcome === 'DECLINED' ? 'warning.light' : 'divider',
                  bgcolor: enc.outcome === 'DECLINED' ? 'warning.50' : 'grey.50',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                  {enc.outcome === 'COMPLETED' ? (
                    <CheckCircleIcon sx={{ fontSize: '0.9rem', color: 'success.main' }} />
                  ) : enc.outcome === 'DECLINED' ? (
                    <CancelIcon sx={{ fontSize: '0.9rem', color: 'warning.main' }} />
                  ) : (
                    <CheckCircleIcon sx={{ fontSize: '0.9rem', color: 'primary.main' }} />
                  )}
                  <Typography variant="body2" fontWeight={700}>
                    {enc.program}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {new Date(enc.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </Typography>
                  <Chip
                    label={enc.outcome === 'COMPLETED' ? 'Completed' : enc.outcome === 'DECLINED' ? 'Declined' : 'Accepted'}
                    size="small"
                    color={enc.outcome === 'COMPLETED' ? 'success' : enc.outcome === 'DECLINED' ? 'warning' : 'primary'}
                    sx={{ fontSize: '0.6rem', height: 18, fontWeight: 700 }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, mb: enc.declineReason ? 1 : 0 }}>
                  {enc.summary}
                </Typography>
                {enc.declineReason && (
                  <Box sx={{ mt: 1, pl: 1.5, borderLeft: '2px solid', borderColor: 'warning.main' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Decline reason:
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {enc.declineReason}
                    </Typography>
                  </Box>
                )}
                {enc.assignedProvider && (
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75 }}>
                    Assigned provider: {enc.assignedProvider}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Coordinator Notes */}
      <Box>
        <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: 1.2 }}>
          Coordinator Notes
        </Typography>
        <Divider sx={{ mt: 1, mb: 2, borderBottomWidth: 2 }} />
        <TextField
          multiline
          minRows={3}
          fullWidth
          placeholder="Add clinical discussion notes, follow-up items, or observations for this referral…"
          value={coordinatorNotes}
          onChange={(e) => setCoordinatorNotes(e.target.value)}
          size="small"
          sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.9rem' } }}
        />
      </Box>
    </Box>
  );
};

export default ClinicalSummaryCard;
